import { type TypedDocumentNode, gql } from '@apollo/client';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineS } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';

import { Games } from '../Games';
import type { GamesNavigator_anyGameInterface } from './__generated__/index.graphql';

const StyledHorizontal = styled(SBHorizontal)`
  padding: var(--intermediate-unit) var(--double-unit);
  background: var(--c-nd-50);
  border-bottom: 1px solid var(--c-black);
  border-radius: var(--double-unit);
`;

const GameSkeleton = styled(Skeleton)`
  border-radius: var(--double-unit);
  height: 150px;
  width: 100%;
`;

type Props = {
  displayName?: string;
  loading: boolean;
  loadPage: (direction: 'previous' | 'next') => void;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  pastGames: GamesNavigator_anyGameInterface[];
  futureGames: GamesNavigator_anyGameInterface[];
  pageSize: number;
  onClick?: (id: string) => void;
};
export const GamesNavigator = ({
  displayName,
  loading,
  hasNextPage,
  hasPreviousPage,
  loadPage,
  pastGames,
  futureGames,
  pageSize,
  onClick,
}: Props) => {
  const { sport } = useSportContext();

  return (
    <FullWidthContainer>
      <Vertical gap={1.5}>
        <HeadlineS as="h2">
          {displayName ? (
            <FormattedMessage
              id="Seo.GamesNavigator.nameFixtures"
              defaultMessage="{displayName} fixtures"
              values={{ displayName }}
            />
          ) : (
            <FormattedMessage
              {...(sport === Sport.FOOTBALL
                ? navLabels.fixtures
                : glossary.games)}
            />
          )}
        </HeadlineS>
        <Vertical>
          <StyledHorizontal>
            <IconButton
              size="small"
              color="tertiary"
              icon={faChevronLeft}
              onClick={() => loadPage('previous')}
              disabled={!hasPreviousPage}
            />
            <BodyM as="p">
              <FormattedMessage
                {...(sport === Sport.FOOTBALL
                  ? navLabels.fixtures
                  : glossary.games)}
              />
            </BodyM>
            <IconButton
              size="small"
              color="tertiary"
              icon={faChevronRight}
              onClick={() => loadPage('next')}
              disabled={!hasNextPage}
            />
          </StyledHorizontal>

          {loading ? (
            <>
              {Array.from({ length: pageSize }, (_, i) => i).map(i => (
                <GameSkeleton key={i} />
              ))}
            </>
          ) : (
            <Games
              games={[...[...pastGames].reverse(), ...futureGames]}
              onClick={onClick}
            />
          )}
        </Vertical>
      </Vertical>
    </FullWidthContainer>
  );
};

GamesNavigator.fragments = {
  anyGameInterface: gql`
    fragment GamesNavigator_anyGameInterface on AnyGameInterface {
      id
      ...Games_anyGameInterface
    }
    ${Games.fragments.anyGameInterface}
  ` as TypedDocumentNode<GamesNavigator_anyGameInterface>,
};
